/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Divider} from '@material-ui/core';
import {useNoop} from '@ion/core/src/hooks/usenoop';
import {GenericObject} from '@ion/core/src/types/entities';
import {
  AssetListSidePanelHeader,
  AssetUpdatesControlProps
} from '@ion/dls/src/components/sidepanel/current/AssetListSidePanelHeader';
import {ASSET_UPDATE_INTERVAL_SECONDS} from '@ion/dls/src/components/sidepanel/current/AssetUpdateProgressBar';
import {ResultsModule} from '@ion/dls/src/components/sidepanel/ResultsModule';
import {SelectedFilters} from '@ion/dls/src/filters/SelectedFilters';
import {useFilterState} from '@ion/dls/src/filters/useFilterState';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import Render from '@ion/dls/src/render/Render';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {isCurrentTheme} from '@ion/dls/src/theme/useThemeContext';
import {Filters} from '@ion/dls/src/types/types';
import {resetValue} from '@ion/dls/src/utils/ComponentUtils';
import {useTracking2RefreshCycle} from '@/routes/tracking2/hooks/useTracking2RefreshCycle';
import {UPDATE_GROUP_FILTERS_CHECKED} from '@/store/actions/groups-filter';
import {
  gridOptionsSelector,
  isUserProfileViewSelector,
  mobileSideSheetOpenSelector
} from '@/store/selectors/nav-selectors';
import {
  findNearbyIsActiveSelector,
  isLargeFleetModeSelector,
  sideSheetViewSelector,
  trackingAssetUpdatesEnabledSelector,
  trackingFiltersSelector,
  trackingFleetUpdateResetFlagSelector
} from '@/store/selectors/tracking-selectors';
import {isFromGroupsAssetsFilter} from '../../../components/filter/helpers';
import FilterActions from '../../../components/filter/TrackingFilters';
import {
  resetLargeFleetAssets,
  setFleetUpdateFlag,
  setTrackingAssetUpdatesPlaybackState
} from '../../../store/actions/tracking';
import useToggleSideSheet from '../hooks/tracking/useToggleSideSheet';
import {useLegacyRefreshCycle} from './useLegacyRefreshCycle';
import useSheetHeader from './useSheetHeader';

export interface ResultsFiltersWrapperProps {
  sort: (e: React.MouseEvent<Element, MouseEvent>) => void;
}

interface ResultsFiltersContainerProps {
  findNearbyBreadcrumb?: string | React.JSX.Element;
  useHeader?: Function;
  listOptions?: GenericObject;
  isTracking2?: boolean;
};

/**
 * @ResultsFiltersContainer
 * Returns a Results and selected Filters Wrapper component. it is responsible for rendering
 * the components based on selection from trackingFilters and view mode.
 *  ```typescript
 *   import ResultsFiltersWrapper from '@/routes/tracking/sidesheet/ResultsFiltersWrapper';
 *
 *   <ResultsFiltersContainer sort={onSort}/>
 *  ```
 */
const ResultsFiltersContainer = ({
  findNearbyBreadcrumb,
  listOptions,
  useHeader = useSheetHeader,
  isTracking2 = false
}: ResultsFiltersContainerProps) => {
  const dispatch                      = useDispatch();
  const noop                          = useNoop();
  const {isPhone, isDesktop}          = useMedia();
  const isUserProfileView             = useSelector(isUserProfileViewSelector);
  const isLargeFleetMode              = useSelector(isLargeFleetModeSelector);
  const sideSheetView                 = useSelector(sideSheetViewSelector);
  const trackingFilters               = useSelector(trackingFiltersSelector);
  const mobileSideSheetOpen           = useSelector(mobileSideSheetOpenSelector);
  const gridOptions                   = useSelector(gridOptionsSelector);
  const findNearbyIsActive            = useSelector(findNearbyIsActiveSelector);
  const isAssetUpdatesEnabled         = useSelector(trackingAssetUpdatesEnabledSelector);
  const fleetUpdateResetFlag          = useSelector(trackingFleetUpdateResetFlagSelector);
  const [filterState, setFilterState] = useFilterState(trackingFilters);
  const {
    onGeofenceOrLocationSearch,
    resultModuleLabel,
    toggleListView,
    onRefreshAssets,
    onSort,
    isAssetsSortDescending
  } = useHeader(listOptions);

  const {
    isDesktopSideSheetOpen,
    onToggleClick,
    onCloseSideSheet
  } = useToggleSideSheet();

  // AssetUpdateProgressBar is the single source of truth for this cycle - no
  // separate interval/timer drives it. Its own CSS fill animation runs only
  // while isPlaying (paused freezes it exactly in place, and CSS correctly
  // counts only the time it actually spent running toward the duration - not
  // wall-clock time), and fires onCycleComplete exactly once it reaches
  // 100%. /tracking and /tracking2 drive that completion very differently
  // (a single owned fetch vs. waiting on every other consumer's shared
  // registry), so each gets its own hook - only one is ever actually in play
  // for a given mount, picked by isTracking2 below. Both are called
  // unconditionally (Rules of Hooks) ...
  const legacyRefreshCycle     = useLegacyRefreshCycle(onRefreshAssets);
  const tracking2RefreshCycle  = useTracking2RefreshCycle(!!listOptions?.isReloading);
  const {
    isRefreshFetching,
    updateCycleKey,
    onProgressCycleComplete
  } = isTracking2 ? tracking2RefreshCycle : legacyRefreshCycle;

  // the progress bar that drives /tracking2's refresh cycle is current theme only, and the
  // interval that used to drive it for every theme (`useFleetUpdateDriver`) was removed with it
  // (ION-7392) - so legacy never refreshed the grid or the map (no /v2/cvs requests). Legacy gets
  // that interval back: every 12 seconds, while Live Updates is on, it pulses the same fleet
  // update flag the grid, map and list react to - on a plain interval, as before, rather than
  // waiting on the other consumers to settle ...
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (isTracking2 && !isCurrentTheme() && isAssetUpdatesEnabled && fleetUpdateResetFlag) {
      interval = setInterval(function() {
        resetValue({
          initialValue : true,
          resetValue   : false,
          fnCall       : function(value: boolean) {
            dispatch(setFleetUpdateFlag(value));
          }
        });
      }, ASSET_UPDATE_INTERVAL_SECONDS * 1000);
    }
    return function() {
      clearInterval(interval);
    };
  }, [isTracking2, isAssetUpdatesEnabled, fleetUpdateResetFlag]);

  const deletionAttributeMap = {
    [Filters.groups]  : 'selectedGroups',
    [Filters.assets]  : 'selectedAssets',
    [Filters.devices] : 'selectedDevices'
  };

  // just the user's play/pause choice - the bar itself (now the sole
  // driver of the refresh cycle, via onProgressCycleComplete) reacts to
  // isAssetUpdatesEnabled on its own: pausing freezes it in place, and
  // resuming continues from that exact position rather than restarting or
  // fetching immediately - the next fetch only fires once it naturally
  // reaches the end again ...
  const onAssetUpdatesPlayback = useCallback(state => {
    dispatch(setTrackingAssetUpdatesPlaybackState(state));
  }, [dispatch]);

  // grouped into one object - AssetListSidePanelHeader's auto-refresh
  // subsystem (Play/Pause pill plus progress bar) is a single cohesive prop
  // concern distinct from its title-row sort/close props ...
  const updateProgress: AssetUpdatesControlProps = {
    isAssetUpdatesEnabled,
    isDataLoading : isRefreshFetching,
    onAssetUpdatesPlayback,
    onProgressCycleComplete,
    updateCycleKey
  };

  const createFilterState = (selected, id, trackingFilters) => {
    if (isFromGroupsAssetsFilter(id)) {
      const {
        selectedGroups,
        selectedAssets,
        selectedDevices
      } = selected || {};
      const state = {
        groups  : {...trackingFilters.groups, selected: selectedGroups?? trackingFilters.groups.selected},
        assets  : {...trackingFilters.assets, selected: selectedAssets?? trackingFilters.assets.selected},
        devices : {...trackingFilters.devices, selected: selectedDevices?? trackingFilters.devices.selected}
      };
      return {
        ...trackingFilters,
        ...state
      };
    }
    return {
      ...trackingFilters,
      [id] : {
        ...trackingFilters[id],
        selected
      }
    };
  };

  const handleDelete = useCallback((id, value, selected) => {
    const action = FilterActions[id];
    if (selected && deletionAttributeMap[id]) {
      const valueToApply = deletionAttributeMap[id]
        ? {[deletionAttributeMap[id]]: value} : value;
      const newState  = createFilterState(valueToApply, id, filterState);
      setFilterState(newState);
      const action = FilterActions[id];
      if (isFromGroupsAssetsFilter(id)) {
        const selected = {
          selectedAssets  : newState.assets.selected,
          selectedGroups  : newState.groups.selected,
          selectedDevices : newState.devices.selected
        };
        action && dispatch(action(selected));
      }
      deletionAttributeMap[id] &&
      dispatch({type: UPDATE_GROUP_FILTERS_CHECKED, item: {id, value, selected}});
      dispatch(resetLargeFleetAssets());
    } else {
      action && dispatch(action(value));
    }
  }, []);

  const canShowSelectedFilters = isPhone && !isUserProfileView;
  const canShowResultFilters   = isPhone && findNearbyIsActive;
  const isRefreshEnabled       = isLargeFleetMode && !isTracking2;

  if (!canShowResultFilters) {
    return (
      <>
        <Render>
          <Render.If condition={isCurrentTheme()}>
            <AssetListSidePanelHeader label={resultModuleLabel}
              onSort={onSort}
              sortDescending={isAssetsSortDescending}
              onClose={onCloseSideSheet}
              updateProgress={updateProgress} />
          </Render.If>
          <Render.Else>
            <ResultsModule label={resultModuleLabel}
              customLabel={findNearbyBreadcrumb}
              onSort={onSort}
              onView={toggleListView}
              gridOptions={gridOptions}
              onRefresh={onRefreshAssets}
              isRefreshEnabled={isRefreshEnabled}
              isTableViewEnabled={isDesktop}
              mobileSideSheetClose={onGeofenceOrLocationSearch}
              mobileSideSheetOpen={mobileSideSheetOpen}
              sideSheetView={sideSheetView}
              onToggleClick={onToggleClick}
              isDesktopSideSheetOpen={isDesktopSideSheetOpen}
              backgroundColor={dlsColor('white')}
              isAssetUpdatesEnabled={isAssetUpdatesEnabled}
              onAssetUpdatesPlayback={onAssetUpdatesPlayback}
              updateProgress={updateProgress}
              isTracking2
              fullHeight />
          </Render.Else>
        </Render>
        <Divider />
        {canShowSelectedFilters && (
          <SelectedFilters filters={trackingFilters as any}
            clearAll={noop}
            onDelete={handleDelete}
            numberChipsLimit={0} />
        )}
      </>
    );
  }
};
export default React.memo(ResultsFiltersContainer);
